import { T } from '@tolgee/react';
import { useCallback } from 'react';
import styled from 'styled-components';

import { CheckSvg } from '../../../assets/icons';
import { ChatBubbleSvg } from '../../../assets/icons/ChatBubbleSvg';
import theme from '../../../assets/theme';
import { DesignerActions, useDesignerContext } from '../../../hooks/useDesignerState';
import { PollOptions } from '../../SlideComponents/slide.types';

type Props = {
  pollOptions: PollOptions[];
  // When true, clicking the Explanation badge opens the popover via reducer.
  // Disabled in static thumbnails (where there's no designer state).
  interactive?: boolean;
};

const SLIDE_PADDING_RATIO = 0.05;
// createPollElements subtracts the gap TWICE from each element's width
// (once when dividing the row, then once more for the element itself), so
// each option bar is `2 * GAP_RATIO` narrower than the per-column slot. We
// match that here to keep the centered Correct / Explanation pills aligned
// with the actual bar center.
const GAP_RATIO = 10 / 1920;
const CORRECT_PILL_TOP_RATIO = 0.71; // 70% from top
const EXPLANATION_PILL_TOP_RATIO = 0.65; // 65% from top — above the Correct pill

const MultipleChoiceBadgeOverlay = ({ pollOptions, interactive = true }: Props) => {
  const dispatch = useDesignerContext(c => c.dispatch);
  // Stable identity for the click handler so each ExplanationPill doesn't
  // get a new `onClick` reference on every render of the overlay. Declared
  // *before* the empty-pollOptions early return so the hook count stays
  // constant across renders (Rules of Hooks).
  const handleExplanationClick = useCallback(
    (optionId: string) => {
      if (!interactive) return;
      dispatch({
        type: DesignerActions.openMcExplanationEditor,
        optionId,
      });
    },
    [interactive, dispatch],
  );
  const count = pollOptions.length;
  if (!count) return null;

  return (
    <Overlay>
      {pollOptions.map((option, index) => {
        const columnLeftPct =
          (SLIDE_PADDING_RATIO + (index * (1 - 2 * SLIDE_PADDING_RATIO)) / count) * 100;
        const columnWidthPct =
          ((1 - 2 * SLIDE_PADDING_RATIO) / count - 2 * GAP_RATIO) * 100;

        return (
          <ColumnContainer
            key={option.id}
            style={{
              left: `${columnLeftPct}%`,
              width: `${columnWidthPct}%`,
            }}
          >
            {option.isCorrect && (
              <CorrectPill style={{ top: `${CORRECT_PILL_TOP_RATIO * 100}%` }}>
                <CheckSvg />
                <T keyName="Correct" defaultValue="Correct" />
              </CorrectPill>
            )}
            {option.explanation && (
              <ExplanationPill
                onClick={e => {
                  e.stopPropagation();
                  handleExplanationClick(option.id);
                }}
                style={{ top: `${EXPLANATION_PILL_TOP_RATIO * 100}%` }}
                $interactive={interactive}
              >
                <ChatBubbleSvg />
                <T keyName="Explanation" defaultValue="Explanation" />
              </ExplanationPill>
            )}
          </ColumnContainer>
        );
      })}
    </Overlay>
  );
};

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 2;
`;

const ColumnContainer = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  pointer-events: none;
`;

const CorrectPill = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #e5f5ee;
  color: #0d7e64;
  border: 1px solid #a8ddcc;
  font-weight: 900;
  font-size: 28px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 8px 22px 8px 16px;
  border-radius: 999px;
  white-space: nowrap;
  pointer-events: none;
  svg {
    width: 24px;
    height: 24px;
  }
`;

const ExplanationPill = styled.button<{ $interactive: boolean }>`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: ${theme.colors.orange}26;
  color: ${theme.colors.orangeDark};
  border: 1px solid ${theme.colors.orange}66;
  border-radius: 999px;
  padding: 6px 16px 6px 12px;
  font-weight: 900;
  font-size: 26px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  white-space: nowrap;
  cursor: ${({ $interactive }) => ($interactive ? 'pointer' : 'default')};
  pointer-events: ${({ $interactive }) => ($interactive ? 'auto' : 'none')};
  &:hover {
    background: ${({ $interactive }) =>
      $interactive ? `${theme.colors.orange}40` : `${theme.colors.orange}26`};
  }
  svg {
    width: 20px;
    height: 20px;
  }
`;

export default MultipleChoiceBadgeOverlay;
